import { getAudienceCountryCodes } from '@digital-twins/shared-types'
import type { AudienceSummary, FeaturedTwin, TargetingPreferences } from '@/lib/api'

export type AudienceMatchTier =
  | 'direct_industry_country'
  | 'close_parent_industry_country'
  | 'broad_parent_industry_country'
  | 'direct_industry'
  | 'close_parent_industry'
  | 'broad_parent_industry'
  | 'country_type'
  | 'audience_type'
  | 'fallback'

export interface RankedAudience {
  audience: AudienceSummary
  score: number
  matchTier: AudienceMatchTier
  originalIndex: number
}

interface SelectRelevantFeaturedTwinsArgs {
  existingTwins: Array<FeaturedTwin>
  marketplaceTwins: Array<FeaturedTwin>
  rankedAudiences: Array<RankedAudience>
  limit: number
  hasIndustryTargeting?: boolean
}

interface SelectRecommendedAudiencesOptions {
  hasIndustryTargeting?: boolean
}

export const PRIMARY_AUDIENCE_SCORE = 500
export const MAX_RECOMMENDED_AUDIENCES = 5
export const MIN_PRIMARY_AUDIENCES_BEFORE_NO_BACKFILL = 3

const INDUSTRY_RELEVANT_TIERS = new Set<AudienceMatchTier>([
  'direct_industry_country',
  'close_parent_industry_country',
  'broad_parent_industry_country',
  'direct_industry',
  'close_parent_industry',
  'broad_parent_industry',
])

export function rankAudiencesByRelevance(
  audiences: Array<AudienceSummary>,
  targeting: TargetingPreferences | null | undefined,
): Array<RankedAudience> {
  return audiences
    .map((audience, originalIndex) => {
      const { matchTier, score } = scoreAudience(audience, targeting)
      return { audience, score, matchTier, originalIndex }
    })
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score
      const leftBookmarked = left.audience.isBookmarked === true ? 1 : 0
      const rightBookmarked = right.audience.isBookmarked === true ? 1 : 0
      if (rightBookmarked !== leftBookmarked) return rightBookmarked - leftBookmarked
      return left.originalIndex - right.originalIndex
    })
}

export function selectRecommendedAudiences(
  rankedAudiences: Array<RankedAudience>,
  options: SelectRecommendedAudiencesOptions = {},
): Array<RankedAudience> {
  if (options.hasIndustryTargeting === true) {
    return rankedAudiences
      .filter((entry) => isIndustryRelevantTier(entry.matchTier))
      .slice(0, MAX_RECOMMENDED_AUDIENCES)
  }

  const primary = rankedAudiences.filter((entry) => entry.score >= PRIMARY_AUDIENCE_SCORE)
  if (primary.length >= MIN_PRIMARY_AUDIENCES_BEFORE_NO_BACKFILL) {
    return primary.slice(0, MAX_RECOMMENDED_AUDIENCES)
  }

  const primaryIds = new Set(primary.map((entry) => entry.audience.id))
  const backfill = rankedAudiences.filter(
    (entry) => entry.score > 0 && !primaryIds.has(entry.audience.id),
  )

  return [...primary, ...backfill].slice(0, MAX_RECOMMENDED_AUDIENCES)
}

export function hasIndustryTargeting(
  targeting: TargetingPreferences | null | undefined,
): boolean {
  const preferences = targeting?.targetingPreferences
  return (
    (preferences?.industryTokens?.length ?? 0) > 0 ||
    (preferences?.industryAncestorSourceIds?.length ?? 0) > 0
  )
}

export function isIndustryRelevantTier(matchTier: AudienceMatchTier): boolean {
  return INDUSTRY_RELEVANT_TIERS.has(matchTier)
}

/**
 * Returns the shared recommended audience set for personalized surfaces.
 */
export function getRelevantAudiences(
  audiences: Array<AudienceSummary>,
  targeting: TargetingPreferences | null | undefined,
): Array<AudienceSummary> {
  const ranked = rankAudiencesByRelevance(audiences, targeting)
  return selectRecommendedAudiences(ranked, {
    hasIndustryTargeting: hasIndustryTargeting(targeting),
  }).map((entry) => entry.audience)
}

export function selectRelevantFeaturedTwins({
  existingTwins,
  marketplaceTwins,
  rankedAudiences,
  limit,
  hasIndustryTargeting,
}: SelectRelevantFeaturedTwinsArgs): Array<FeaturedTwin> {
  const pinned = getDistinctByPersona(existingTwins.filter((twin) => twin.pinned === true))
  const remaining = Math.max(0, limit - pinned.length)
  if (remaining === 0) return pinned.slice(0, limit)

  const pinnedIds = new Set(pinned.map((twin) => twin.personaId))

  const byAudience = new Map<string, Array<FeaturedTwin>>()
  for (const twin of marketplaceTwins) {
    if (!twin.populationId || pinnedIds.has(twin.personaId)) continue
    const group = byAudience.get(twin.populationId) ?? []
    group.push(twin)
    byAudience.set(twin.populationId, group)
  }

  const selected: Array<FeaturedTwin> = []
  const recommendedAudiences = selectRecommendedAudiences(rankedAudiences, {
    hasIndustryTargeting,
  })

  for (const rankedAudience of recommendedAudiences) {
    for (const twin of byAudience.get(rankedAudience.audience.id) ?? []) {
      if (hasSelectedPersona([...pinned, ...selected], twin.personaId)) continue
      selected.push(twin)
      if (selected.length >= remaining) return [...pinned, ...selected]
    }
  }

  return [...pinned, ...selected]
}

/**
 * Tier-weighted scoring: deeper industry matches score higher than broad ones.
 *
 * "Direct" = audience has the exact leaf sourceId the user selected.
 * "Close parent" = audience matches at tier >= 2 (e.g. "Snacks" when user picked "Savory Snacks").
 * "Broad parent" = audience matches only at tier 1 (e.g. "Food & Beverage").
 *
 * | Match                            | Score |
 * |----------------------------------|-------|
 * | direct_industry + country        | 1000  |
 * | close_parent_industry + country  |  800  |
 * | broad_parent_industry + country  |  600  |
 * | direct_industry                  |  500  |
 * | close_parent_industry            |  400  |
 * | broad_parent_industry            |  300  |
 * | country + matching type          |  200  |
 * | matching type only               |  100  |
 * | fallback                         |    0  |
 */
function scoreAudience(
  audience: AudienceSummary,
  targeting: TargetingPreferences | null | undefined,
): { matchTier: AudienceMatchTier; score: number } {
  const preferences = targeting?.targetingPreferences
  const selectedIndustries = new Set(preferences?.industryTokens ?? [])
  const selectedAncestors = new Set(preferences?.industryAncestorSourceIds ?? [])
  const selectedCountries = new Set(preferences?.focusMarkets ?? [])
  const hasIndustryPrefs = selectedIndustries.size > 0
  const hasCountryPrefs = selectedCountries.size > 0
  const countryHit = hasCountryPrefs && getAudienceCountryCodes(audience).some((code) => selectedCountries.has(code))
  const directIndustryHit =
    hasIndustryPrefs &&
    getAudienceIndustryIdentifiers(audience).some((id) => selectedIndustries.has(id))
  const parentIndustryDepth = selectedAncestors.size > 0
    ? getDeepestAncestorTier(audience, selectedAncestors)
    : 0
  const audienceTypeHit =
    targeting?.targetingType === 'BOTH' ||
    (targeting?.targetingType != null && audience.audienceType === targeting.targetingType)

  if (directIndustryHit && countryHit) return { matchTier: 'direct_industry_country', score: 1000 }
  if (parentIndustryDepth >= 2 && countryHit) return { matchTier: 'close_parent_industry_country', score: 800 }
  if (parentIndustryDepth >= 1 && countryHit) return { matchTier: 'broad_parent_industry_country', score: 600 }
  if (directIndustryHit) return { matchTier: 'direct_industry', score: 500 }
  if (parentIndustryDepth >= 2) return { matchTier: 'close_parent_industry', score: 400 }
  if (parentIndustryDepth >= 1) return { matchTier: 'broad_parent_industry', score: 300 }
  if (countryHit && audienceTypeHit) return { matchTier: 'country_type', score: 200 }
  if (audienceTypeHit) return { matchTier: 'audience_type', score: 100 }
  return { matchTier: 'fallback', score: 0 }
}

/**
 * Returns the deepest (highest tier number) ancestor match between the
 * audience's industry labels and the user's selected ancestor identifiers.
 * Checks both sourceId and token to handle either identifier format.
 * Returns 0 if no ancestor matches.
 */
function getDeepestAncestorTier(
  audience: AudienceSummary,
  selectedAncestors: Set<string>,
): number {
  let deepest = 0
  for (const label of audience.industryLabels ?? []) {
    const hit =
      (label.sourceId && selectedAncestors.has(label.sourceId)) ||
      (label.token && selectedAncestors.has(label.token))
    if (hit) {
      deepest = Math.max(deepest, label.tier ?? 0)
    }
  }
  return deepest
}

/**
 * Collects all matchable industry identifiers for an audience.
 * Includes both sourceId AND token because user preferences store tokens
 * (from the taxonomy picker) while labels carry both fields.
 */
function getAudienceIndustryIdentifiers(audience: AudienceSummary): Array<string> {
  const ids: Array<string> = []
  if (audience.industryLabel?.sourceId) ids.push(audience.industryLabel.sourceId)
  if (audience.industryLabel?.token) ids.push(audience.industryLabel.token)
  for (const label of audience.industryLabels ?? []) {
    if (label.sourceId) ids.push(label.sourceId)
    if (label.token) ids.push(label.token)
  }
  return [...new Set(ids)]
}

/** The rule lives in `@digital-twins/shared-types` (one source with the backend). */
export { getAudienceCountryCodes }

function hasSelectedPersona(selected: Array<FeaturedTwin>, personaId: string): boolean {
  if (!isValidPersonaId(personaId)) return false
  return selected.some((item) => item.personaId === personaId)
}

function isValidPersonaId(personaId: string | null | undefined): personaId is string {
  return Boolean(
    personaId &&
      personaId.length > 0 &&
      personaId !== 'null' &&
      personaId !== 'undefined',
  )
}

function getDistinctByPersona(twins: Array<FeaturedTwin>): Array<FeaturedTwin> {
  const seen = new Set<string>()
  return twins.filter((twin) => {
    if (!isValidPersonaId(twin.personaId)) return true
    if (seen.has(twin.personaId)) return false
    seen.add(twin.personaId)
    return true
  })
}
